import { AxiosResponse } from 'axios';
import i18next from 'i18next';
import { hideLoading, showLoading } from 'react-redux-loading-bar';
import { replace } from 'react-router-redux';
import { call, put } from 'redux-saga/effects';
import { store } from 'src/app/store';
import CheckCircleBig from 'src/assets/check-circle-big.svg';
import instance, { CustomAxiosError } from 'src/axios';
import {
  ApiPaginationResponse,
  TableQueryParameters,
} from 'src/components/ui/Table/Table.interface';
import { apiBaseRoutes, apiRoutes } from 'src/constants/apiConstants';
import { Errors } from 'src/constants/errorConstants';
import {
  addError,
  addModal,
  addSnackbar,
  removeModal,
  setGlobalLoading,
  startLoading,
  stopLoading,
} from 'src/features/globalSlice';
import { routes } from 'src/routes';
import { cleanQueryParams } from 'src/utils/useQueryParams';

import {
  addCompany,
  editCompany,
  getCompanyDetails,
  getCompanyList,
  removeCompany,
  setCompanyDetails,
  setCompanyList,
} from '../actions/companyActions';
import { Company, ListCompany } from '../models/Company';
import { ModalType } from '../models/Modal';
import { SnackbarType } from '../models/Snackbar';
import { ApiResponse } from '.';

/* Get company list */
export function* CompanyGetListSaga(
  action: ReturnType<typeof getCompanyList>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const paramsString = cleanQueryParams<
      TableQueryParameters<void, ListCompany>
    >(action.payload.params);

    const response = (yield instance.get(
      `${apiBaseRoutes.COMPANY}${paramsString}`
    )) as AxiosResponse<ApiPaginationResponse<Company[]>>;

    yield put(
      setCompanyList({
        ...response.data,
        data: response.data?.data.map((c) => {
          return {
            ...c,
            personnelCount:
              (c?.base ? c._count?.PortalUser - 1 : c?._count?.PortalUser) ?? 0,
          };
        }),
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Get company details */
export function* CompanyGetDetailsSaga(
  action: ReturnType<typeof getCompanyDetails>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const response = (yield instance.get(
      `${apiBaseRoutes.COMPANY}/${action.payload?.companyId}`
    )) as AxiosResponse<ApiResponse<Company>>;

    yield put(setCompanyDetails(response.data?.data));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Add company helper */
export function* AddCompanyHelper(company: Company): Generator {
  try {
    const response = (yield instance.post(`${apiBaseRoutes.COMPANY}`, {
      ...company,
      website: company?.website || null,
    })) as AxiosResponse<ApiResponse<Company>>;

    return response;
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    if (typeof error.response?.data?.message === 'string') {
      yield put(
        addSnackbar({
          type: SnackbarType.ERROR,
          body: errorMessage,
          snackKey: Date.now(),
        })
      );
    } else {
      yield put(
        addError({
          actionType: addCompany.type,
          error: errorMessage,
        })
      );
    }
  }
}

/* Add company */
export function* CompanyAddNewSaga(
  action: ReturnType<typeof addCompany>
): Generator {
  yield put(setGlobalLoading(true));
  yield put(startLoading({ actionType: action.type }));
  try {
    const response = (yield call(
      AddCompanyHelper,
      action?.payload
    )) as AxiosResponse<ApiResponse<Company>>;

    if (response) {
      yield put(
        addModal({
          title: i18next.t('service_account:company_added'),
          body: i18next.t('service_account:company_added_details', {
            title: action.payload?.title,
          }),
          modalType: ModalType.SUCCESS,
          icon: CheckCircleBig,
          onBackdropClose: false,
          primaryButtonText: i18next.t('service_account:create_personnel'),
          secondaryButtonText: i18next.t('service_account:back_to_companies'),
          primaryAction: () => {
            store.dispatch(removeModal());
            store.dispatch(
              replace(routes.NEW_SERVICE_ACCOUNT, {
                forcePush: true,
                company: response?.data?.data,
              })
            );
          },
          secondaryAction: () => {
            store.dispatch(removeModal());
            store.dispatch(replace(routes.COMPANIES, { forcePush: true }));
          },
        })
      );
    }
  } catch (e) {
    yield put(removeModal());
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(stopLoading({ actionType: action.type }));
    yield put(setGlobalLoading(false));
  }
}

/* Edit company helper */
export function* EditCompanyHelper(company: Company): Generator {
  try {
    const response = (yield instance.put(
      `${apiBaseRoutes.COMPANY}/${company?.id}`,
      { ...company, website: company?.website || null, _count: undefined }
    )) as AxiosResponse<ApiResponse<Company>>;

    return response;
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    if (typeof error.response?.data?.message === 'string') {
      yield put(
        addSnackbar({
          type: SnackbarType.ERROR,
          body: errorMessage,
          snackKey: Date.now(),
        })
      );
    } else {
      yield put(
        addError({
          actionType: editCompany.type,
          error: errorMessage,
        })
      );
    }
  }
}

/* Edit company */
export function* CompanyEditSaga(
  action: ReturnType<typeof editCompany>
): Generator {
  yield put(setGlobalLoading(true));
  yield put(startLoading({ actionType: action.type }));
  try {
    const response = (yield call(
      EditCompanyHelper,
      action?.payload
    )) as AxiosResponse<ApiResponse<Company>>;

    if (response) {
      yield put(setCompanyDetails(response?.data?.data));
      yield put(
        addSnackbar({
          type: SnackbarType.SUCCESS,
          title: i18next.t('common:changes_saved'),
          body: i18next.t('common:changes_saved_success'),
          snackKey: Date.now(),
        })
      );
    }
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(setGlobalLoading(false));
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Remove company */
export function* CompanyRemoveSaga(
  action: ReturnType<typeof removeCompany>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    (yield instance.delete(
      `${apiBaseRoutes.COMPANY}/${action.payload?.company?.id}${
        action?.payload?.fullRemove ? apiRoutes.FULL : ''
      }`
    )) as AxiosResponse<ApiResponse<Company>>;

    yield put(removeModal());
    yield put(replace(routes.COMPANIES, { forcePush: true }));
    yield put(
      addSnackbar({
        type: SnackbarType.SUCCESS,
        title: i18next.t('service_account:company_removed'),
        body: i18next.t('service_account:company_removed_details', {
          title: action.payload?.company?.title,
        }),
        snackKey: Date.now(),
      })
    );
  } catch (e) {
    yield put(removeModal());
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}
